import { useCallback, useContext, useEffect, useState } from 'react'

import { useIdleTimer } from 'react-idle-timer'
import * as z from 'zod'

import { routes } from '@cedarjs/router'

import { usePatientPortalAuth } from 'src/auth'
import Box from 'src/components/atoms/Box'
import LoadingSpinner from 'src/components/atoms/LoadingSpinner/LoadingSpinner'
import { KioskCancelCheckInDialog } from 'src/components/Kiosk/KioskCancelCheckInDialog'
import { createNamedContext } from 'src/utils'

const kioskModeStateSchema = z.object({
  portalKey: z.string().min(1),
  appointmentId: z.string().optional(),
  practiceName: z.string().optional(),
})

type KioskModeState = z.infer<typeof kioskModeStateSchema>

export const KIOSK_FOOTER_NOTE =
  "Please don't use the browser's back button. If you need assistance, please see a practice staff member."

export const kioskHeaderTitle = (practiceName: string | undefined) =>
  practiceName ? `${practiceName} check-in` : 'Check-in'

const KIOSK_MODE_STORAGE_KEY = 'kioskMode'

const KIOSK_IDLE_TIMEOUT_MS = 5 * 60 * 1000

export const readKioskMode = (): KioskModeState | null => {
  try {
    const raw = window.sessionStorage.getItem(KIOSK_MODE_STORAGE_KEY)
    if (!raw) return null
    const parsed = kioskModeStateSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

export const setKioskMode = (mode: KioskModeState) => {
  window.sessionStorage.setItem(KIOSK_MODE_STORAGE_KEY, JSON.stringify(mode))
}

export const clearKioskMode = () => {
  window.sessionStorage.removeItem(KIOSK_MODE_STORAGE_KEY)
}

interface KioskModeContextValue {
  isKioskMode: boolean
  kioskTaskHubRoute: string | null
  headerTitle: string | null
  exitToKiosk: (options?: { completed?: boolean }) => Promise<void>
  confirmCancel: () => void
}

const kioskTaskHubRouteFor = (mode: KioskModeState | null) =>
  mode?.appointmentId
    ? routes.kioskTaskHub({ appointmentId: mode.appointmentId })
    : null

const KioskModeContext = createNamedContext<KioskModeContextValue | null>(
  'KioskModeContext'
)

export const useReloadWhenRestoredFromHistory = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return
    const reloadIfRestored = (event: PageTransitionEvent) => {
      if (event.persisted) window.location.reload()
    }
    window.addEventListener('pageshow', reloadIfRestored)
    return () => window.removeEventListener('pageshow', reloadIfRestored)
  }, [enabled])
}

export const KioskModeProvider = ({ children }: React.PropsWithChildren) => {
  const { logOut } = usePatientPortalAuth()

  const [exiting, setExiting] = useState(false)
  const [confirmingCancel, setConfirmingCancel] = useState(false)

  const kioskMode = readKioskMode()
  const isKioskMode = !!kioskMode

  useReloadWhenRestoredFromHistory(isKioskMode)

  const exitToKiosk = useCallback(
    async ({ completed = false }: { completed?: boolean } = {}) => {
      const mode = readKioskMode()
      if (!mode) return

      setConfirmingCancel(false)
      setExiting(true)
      clearKioskMode()
      await logOut().catch(() => null)
      window.location.replace(
        routes.kiosk({
          tenantPortalKey: mode.portalKey,
          ...(completed ? { completed: 'true' } : {}),
        })
      )
    },
    [logOut]
  )

  useIdleTimer({
    timeout: KIOSK_IDLE_TIMEOUT_MS,
    onIdle: () => void exitToKiosk(),
    disabled: !isKioskMode,
    crossTab: false,
  })

  return (
    <KioskModeContext.Provider
      value={{
        isKioskMode,
        kioskTaskHubRoute: kioskTaskHubRouteFor(kioskMode),
        headerTitle: kioskMode
          ? kioskHeaderTitle(kioskMode.practiceName)
          : null,
        exitToKiosk,
        confirmCancel: () => setConfirmingCancel(true),
      }}
    >
      <KioskCancelCheckInDialog
        isOpen={isKioskMode && confirmingCancel}
        setIsOpen={setConfirmingCancel}
        onCancelCheckIn={() => void exitToKiosk()}
      />
      {exiting ? (
        <Box className="flex h-screen items-center justify-center">
          <LoadingSpinner />
        </Box>
      ) : (
        children
      )}
    </KioskModeContext.Provider>
  )
}

export const useKioskMode = (): KioskModeContextValue => {
  const context = useContext(KioskModeContext)
  return (
    context ?? {
      isKioskMode: false,
      kioskTaskHubRoute: null,
      headerTitle: null,
      exitToKiosk: async () => {},
      confirmCancel: () => {},
    }
  )
}
